/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import React, { type JSX } from 'react';

import {
  $createLinkNode,
  $isAutoLinkNode,
  $isLinkNode,
  TOGGLE_LINK_COMMAND,
} from '@lexical/link';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$findMatchingParent, mergeRegister} from '@lexical/utils';
import {
  $getSelection,
  $isLineBreakNode,
  $isNodeSelection,
  $isRangeSelection,
  $isTextNode,
  BaseSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  getDOMSelection,
  KEY_ESCAPE_COMMAND,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import {Dispatch, useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';

import {createPortal} from 'react-dom';

import { defineStyles, useStyles } from '@/components/hooks/useStyles';
import {getSelectedNode} from '../../utils/getSelectedNode';
import {setFloatingElemPositionForLinkEditor} from '../../utils/setFloatingElemPositionForLinkEditor';
import {normalizeUrl, sanitizeUrl} from '../../utils/url';
import { LINK_CHANGE_COMMAND } from '@/components/editor/lexicalPlugins/suggestions/linkChangeSuggestionCommand';
import { PencilFillIcon } from '../../icons/PencilFillIcon';
import { Trash3Icon } from '../../icons/Trash3Icon';
import { SuccessAltIcon } from '../../icons/SuccessAltIcon';
import ForumIcon from '@/components/common/ForumIcon';
import classNames from 'classnames';

const styles = defineStyles('LexicalFloatingLinkEditorPlugin', (theme: ThemeType) => ({
  linkEditor: {
    display: 'flex',
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 10,
    maxWidth: 400,
    width: '100%',
    opacity: 0,
    backgroundColor: theme.palette.grey[0],
    boxShadow: `0 5px 10px ${theme.palette.greyAlpha(0.3)}`,
    borderRadius: '0 0 8px 8px',
    transition: 'opacity 0.5s',
    willChange: 'transform',
  },
  button: {
    width: 20,
    height: 20,
    display: 'inline-block',
    padding: 6,
    borderRadius: 8,
    cursor: 'pointer',
    margin: '0 2px',
    '&.hovered': {
      backgroundColor: theme.palette.grey[200],
    },
    '& i': {
      backgroundSize: 'contain',
      display: 'inline-block',
      height: 20,
      width: 20,
      verticalAlign: '-0.25em',
    },
  },
  // A labelled input row: the pill that used to be the <input> itself is now a
  // <label> wrapping a persistent field name and a borderless input, so the
  // name stays visible once the field has content. Clicking anywhere in the
  // pill focuses the input.
  fieldRow: {
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    margin: '8px 12px',
    padding: '8px 12px',
    borderRadius: 15,
    backgroundColor: theme.palette.grey[200],
    cursor: 'text',
  },
  urlFieldRow: {
    flex: 1,
    minWidth: 0,
  },
  fieldLabel: {
    flexShrink: 0,
    minWidth: 34,
    marginRight: 8,
    paddingRight: 8,
    borderRight: `1px solid ${theme.palette.greyAlpha(0.15)}`,
    fontSize: 13,
    fontWeight: 500,
    color: theme.palette.grey[600],
    userSelect: 'none',
  },
  linkInput: {
    flex: 1,
    minWidth: 0,
    margin: 0,
    padding: 0,
    border: 0,
    outline: 0,
    backgroundColor: 'transparent',
    fontSize: 15,
    color: theme.palette.grey[700],
    fontFamily: 'inherit',
  },
  linkView: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '8px 12px',
    '& a': {
      flex: 1,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      color: theme.palette.primary.main,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
  linkAction: {
    width: 28,
    height: 28,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 4,
    borderRadius: 4,
    cursor: 'pointer',
    marginLeft: 4,
    border: 'none',
    background: 'transparent',
    '&:hover': {
      backgroundColor: theme.palette.grey[200],
    },
  },
  linkIcon: {
    width: 16,
    height: 16,
    display: 'block',
  },
  editContainer: {
    width: '100%',
  },
  editUrlRow: {
    display: 'flex',
    alignItems: 'center',
  },
}));

function preventDefault(
  event: React.KeyboardEvent<HTMLInputElement> | React.MouseEvent<HTMLElement>,
): void {
  event.preventDefault();
}

function FloatingLinkEditor({
  editor,
  isLink,
  setIsLink,
  anchorElem,
  isLinkEditMode,
  setIsLinkEditMode,
  isSuggestionMode,
}: {
  editor: LexicalEditor;
  isLink: boolean;
  setIsLink: Dispatch<boolean>;
  anchorElem: HTMLElement;
  isLinkEditMode: boolean;
  setIsLinkEditMode: Dispatch<boolean>;
  isSuggestionMode: boolean;
}): JSX.Element {
  const classes = useStyles(styles);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Last DOMRect used to position the floating editor. Stored in a ref so we
  // can re-run positioning when the editor's own height changes (e.g. when
  // toggling into edit mode, which adds the URL input row) without needing
  // the Lexical selection to be live -- at that point focus has moved into
  // our own <input>, so the usual $updateLinkEditor path can't recompute it.
  const lastTargetRectRef = useRef<DOMRect | null>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [editedLinkUrl, setEditedLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [editedLinkText, setEditedLinkText] = useState('');
  const [lastSelection, setLastSelection] = useState<BaseSelection | null>(
    null,
  );

  const $updateLinkEditor = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const linkParent = $findMatchingParent(node, $isLinkNode);

      if (linkParent) {
        setLinkUrl(linkParent.getURL());
        setLinkText(linkParent.getTextContent());
      } else if ($isLinkNode(node)) {
        setLinkUrl(node.getURL());
        setLinkText(node.getTextContent());
      } else {
        setLinkUrl('');
        setLinkText(selection.getTextContent());
      }
      if (isLinkEditMode) {
        setEditedLinkUrl(linkUrl);
        setEditedLinkText(linkText);
      }
    } else if ($isNodeSelection(selection)) {
      const nodes = selection.getNodes();
      if (nodes.length > 0) {
        const node = nodes[0];
        const parent = node.getParent();
        if ($isLinkNode(parent)) {
          setLinkUrl(parent.getURL());
          setLinkText(parent.getTextContent());
        } else if ($isLinkNode(node)) {
          setLinkUrl(node.getURL());
          setLinkText(node.getTextContent());
        } else {
          setLinkUrl('');
          setLinkText('');
        }
        if (isLinkEditMode) {
          setEditedLinkUrl(linkUrl);
          setEditedLinkText(linkText);
        }
      }
    }

    const editorElem = editorRef.current;
    const nativeSelection = getDOMSelection(editor._window);
    const activeElement = document.activeElement;

    if (editorElem === null) {
      return;
    }

    const rootElement = editor.getRootElement();

    if (selection !== null && rootElement !== null && editor.isEditable()) {
      let domRect: DOMRect | undefined;

      if ($isNodeSelection(selection)) {
        const nodes = selection.getNodes();
        if (nodes.length > 0) {
          const element = editor.getElementByKey(nodes[0].getKey());
          if (element) {
            domRect = element.getBoundingClientRect();
          }
        }
      } else if (
        nativeSelection !== null &&
        rootElement.contains(nativeSelection.anchorNode)
      ) {
        domRect =
          nativeSelection.focusNode?.parentElement?.getBoundingClientRect();
      }

      if (domRect) {
        lastTargetRectRef.current = domRect;
        setFloatingElemPositionForLinkEditor(domRect, editorElem, anchorElem);
      }
      setLastSelection(selection);
    } else if (!activeElement || activeElement.className !== 'link-input') {
      if (rootElement !== null) {
        setFloatingElemPositionForLinkEditor(null, editorElem, anchorElem);
      }
      lastTargetRectRef.current = null;
      setLastSelection(null);
      setIsLinkEditMode(false);
      setLinkUrl('');
    }

    return true;
  }, [anchorElem, editor, setIsLinkEditMode, isLinkEditMode, linkUrl, linkText]);

  useEffect(() => {
    const scrollerElem = anchorElem.parentElement;

    const update = () => {
      editor.getEditorState().read(() => {
        $updateLinkEditor();
      });
    };

    window.addEventListener('resize', update);

    if (scrollerElem) {
      scrollerElem.addEventListener('scroll', update);
    }

    return () => {
      window.removeEventListener('resize', update);

      if (scrollerElem) {
        scrollerElem.removeEventListener('scroll', update);
      }
    };
  }, [anchorElem.parentElement, editor, $updateLinkEditor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({editorState}) => {
        editorState.read(() => {
          $updateLinkEditor();
        });
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          $updateLinkEditor();
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        KEY_ESCAPE_COMMAND,
        () => {
          if (isLink || isLinkEditMode) {
            setIsLink(false);
            setIsLinkEditMode(false);
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_HIGH,
      ),
    );
  }, [editor, $updateLinkEditor, setIsLink, isLink, isLinkEditMode, setIsLinkEditMode]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      $updateLinkEditor();
    });
  }, [editor, $updateLinkEditor]);

  useEffect(() => {
    if (isLinkEditMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLinkEditMode, isLink]);

  // Toggling into edit mode adds the URL input row, which makes the floating
  // editor taller. Re-run positioning against the last target rect so the
  // flip-above logic in setFloatingElemPositionForLinkEditor has a chance to
  // kick in if the taller editor no longer fits below the link.
  useLayoutEffect(() => {
    const editorElem = editorRef.current;
    const targetRect = lastTargetRectRef.current;
    if (editorElem && targetRect) {
      setFloatingElemPositionForLinkEditor(targetRect, editorElem, anchorElem);
    }
  }, [isLinkEditMode, anchorElem]);

  useEffect(() => {
    const editorElement = editorRef.current;
    if (editorElement === null) {
      return;
    }
    const handleBlur = (event: FocusEvent) => {
      if (!editorElement.contains(event.relatedTarget as Element) && (isLink || isLinkEditMode)) {
        setIsLink(false);
        setIsLinkEditMode(false);
      }
    };
    editorElement.addEventListener('focusout', handleBlur);
    return () => {
      editorElement.removeEventListener('focusout', handleBlur);
    };
  }, [editorRef, setIsLink, setIsLinkEditMode, isLink, isLinkEditMode]);

  const monitorInputInteraction = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'Enter') {
      handleLinkSubmission(event);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setIsLinkEditMode(false);
    }
  };

  const handleTextInputKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      inputRef.current?.focus();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setIsLinkEditMode(false);
    }
  };

  const getCurrentLinkChangePayload = () => {
    return editor.getEditorState().read(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) {
        return { linkNode: null, linkTextNode: null };
      }
      const selectedNode = getSelectedNode(selection);
      const linkNode = $findMatchingParent(selectedNode, $isLinkNode);
      if (!linkNode) {
        return { linkNode: null, linkTextNode: null };
      }
      const firstChild = linkNode.getFirstChild();
      return {
        linkNode,
        linkTextNode: $isTextNode(firstChild) ? firstChild : null,
      };
    });
  };

  const handleLinkSubmission = (
    event:
      | React.KeyboardEvent<HTMLInputElement>
      | React.MouseEvent<HTMLElement>,
  ) => {
    event.preventDefault();
    if (lastSelection !== null) {
      if (editedLinkUrl.trim() !== '') {
        const textChanged = editedLinkText.trim() !== '' && editedLinkText !== linkText;
        if (isSuggestionMode) {
          const { linkNode, linkTextNode } = getCurrentLinkChangePayload();
          editor.dispatchCommand(LINK_CHANGE_COMMAND, {
            text: textChanged ? editedLinkText : null,
            linkNode,
            url: editedLinkUrl,
            linkTextNode,
          });
        } else {
          editor.update(() => {
            editor.dispatchCommand(
              TOGGLE_LINK_COMMAND,
              sanitizeUrl(normalizeUrl(editedLinkUrl)),
            );
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              const parent = getSelectedNode(selection).getParent();
              if ($isAutoLinkNode(parent)) {
                const linkNode = $createLinkNode(parent.getURL(), {
                  rel: parent.__rel,
                  target: parent.__target,
                  title: parent.__title,
                });
                parent.replace(linkNode, true);
              }
            }

            if (textChanged) {
              const sel = $getSelection();
              if ($isRangeSelection(sel)) {
                const node = getSelectedNode(sel);
                const foundLink = $findMatchingParent(node, $isLinkNode) || ($isLinkNode(node) ? node : null);
                if (foundLink) {
                  const children = foundLink.getChildren();
                  const firstChild = children[0];
                  if ($isTextNode(firstChild)) {
                    for (let i = children.length - 1; i > 0; i--) {
                      children[i].remove();
                    }
                    firstChild.setTextContent(editedLinkText);
                    // The existing selection may point past the end of the
                    // newly-shortened text node, which would make Lexical throw
                    // "$getTextNodeOffset: invalid offset" while reconciling.
                    // Re-anchor the selection to the rewritten text node.
                    firstChild.select(editedLinkText.length, editedLinkText.length);
                  }
                }
              }
            }
          });
        }
      }
      setEditedLinkUrl('');
      setEditedLinkText('');
      setIsLinkEditMode(false);
    }
  };

  return (
    <div ref={editorRef} className={classes.linkEditor}>
      {!isLink && !isLinkEditMode ? null : isLinkEditMode ? (
        <div className={classes.editContainer}>
          <label className={classes.fieldRow}>
            <span className={classes.fieldLabel}>Text</span>
            <input
              className={classes.linkInput}
              aria-label="Link text"
              value={editedLinkText}
              onChange={(event) => {
                setEditedLinkText(event.target.value);
              }}
              onKeyDown={handleTextInputKeyDown}
            />
          </label>
          <div className={classes.editUrlRow}>
            <label className={classNames(classes.fieldRow, classes.urlFieldRow)}>
              <span className={classes.fieldLabel}>URL</span>
              <input
                ref={inputRef}
                className={classes.linkInput}
                aria-label="Link URL"
                placeholder="https://example.com"
                value={editedLinkUrl}
                onChange={(event) => {
                  setEditedLinkUrl(event.target.value);
                }}
                onKeyDown={(event) => {
                  monitorInputInteraction(event);
                }}
              />
            </label>
            <button
              className={classes.linkAction}
              type="button"
              tabIndex={0}
              onMouseDown={preventDefault}
              onClick={() => {
                setIsLinkEditMode(false);
              }}
              aria-label="Cancel">
              <ForumIcon icon="Close" className={classes.linkIcon} />
            </button>

            <button
              className={classes.linkAction}
              type="button"
              tabIndex={0}
              onMouseDown={preventDefault}
              onClick={handleLinkSubmission}
              aria-label="Confirm">
              <SuccessAltIcon className={classes.linkIcon} />
            </button>
          </div>
        </div>
      ) : (
        <div className={classes.linkView}>
          <a
            href={sanitizeUrl(normalizeUrl(linkUrl))}
            target="_blank"
            rel="noopener noreferrer">
            {linkUrl}
          </a>
          <button
            className={classes.linkAction}
            type="button"
            tabIndex={0}
            onMouseDown={preventDefault}
            onClick={(event) => {
              event.preventDefault();
              setEditedLinkUrl(linkUrl);
              setEditedLinkText(linkText);
              setIsLinkEditMode(true);
            }}
            aria-label="Edit link">
            <PencilFillIcon className={classes.linkIcon} />
          </button>
          <button
            className={classes.linkAction}
            type="button"
            tabIndex={0}
            onMouseDown={preventDefault}
            onClick={() => {
              if (isSuggestionMode) {
                const { linkNode, linkTextNode } = getCurrentLinkChangePayload();
                editor.dispatchCommand(LINK_CHANGE_COMMAND, {
                  text: null,
                  linkNode,
                  url: null,
                  linkTextNode,
                });
              } else {
                editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
              }
            }}
            aria-label="Remove link">
            <Trash3Icon className={classes.linkIcon} />
          </button>
        </div>
      )}
    </div>
  );
}

function useFloatingLinkEditorToolbar(
  editor: LexicalEditor,
  anchorElem: HTMLElement,
  isLinkEditMode: boolean,
  setIsLinkEditMode: Dispatch<boolean>,
  isSuggestionMode: boolean,
): JSX.Element | null {
  const [activeEditor, setActiveEditor] = useState(editor);
  const [isLink, setIsLink] = useState(false);

  useEffect(() => {
    function $updateToolbar() {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const focusNode = getSelectedNode(selection);
        const focusLinkNode = $findMatchingParent(focusNode, $isLinkNode);
        const focusAutoLinkNode = $findMatchingParent(
          focusNode,
          $isAutoLinkNode,
        );
        if (!(focusLinkNode || focusAutoLinkNode)) {
          setIsLink(false);
          return;
        }
        const badNode = selection
          .getNodes()
          .filter((node) => !$isLineBreakNode(node))
          .find((node) => {
            const linkNode = $findMatchingParent(node, $isLinkNode);
            const autoLinkNode = $findMatchingParent(node, $isAutoLinkNode);
            return (
              (focusLinkNode && !focusLinkNode.is(linkNode)) ||
              (linkNode && !linkNode.is(focusLinkNode)) ||
              (focusAutoLinkNode && !focusAutoLinkNode.is(autoLinkNode)) ||
              (autoLinkNode &&
                (!autoLinkNode.is(focusAutoLinkNode) ||
                  autoLinkNode.getIsUnlinked()))
            );
          });
        if (!badNode) {
          setIsLink(true);
        } else {
          setIsLink(false);
        }
      } else if ($isNodeSelection(selection)) {
        const nodes = selection.getNodes();
        if (nodes.length === 0) {
          setIsLink(false);
          return;
        }
        const node = nodes[0];
        const parent = node.getParent();
        if ($isLinkNode(parent) || $isLinkNode(node)) {
          setIsLink(true);
        } else {
          setIsLink(false);
        }
      }
    }
    return mergeRegister(
      editor.registerUpdateListener(({editorState}) => {
        editorState.read(() => {
          $updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, newEditor) => {
          $updateToolbar();
          setActiveEditor(newEditor);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        CLICK_COMMAND,
        (payload) => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const node = getSelectedNode(selection);
            const linkNode = $findMatchingParent(node, $isLinkNode);
            if ($isLinkNode(linkNode) && (payload.metaKey || payload.ctrlKey)) {
              window.open(linkNode.getURL(), '_blank');
              return true;
            }
          }
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor]);

  return createPortal(
    <FloatingLinkEditor
      editor={activeEditor}
      isLink={isLink}
      anchorElem={anchorElem}
      setIsLink={setIsLink}
      isLinkEditMode={isLinkEditMode}
      setIsLinkEditMode={setIsLinkEditMode}
      isSuggestionMode={isSuggestionMode}
    />,
    anchorElem,
  );
}

export default function FloatingLinkEditorPlugin({
  anchorElem = document.body,
  isLinkEditMode,
  setIsLinkEditMode,
  isSuggestionMode = false,
}: {
  anchorElem?: HTMLElement;
  isLinkEditMode: boolean;
  setIsLinkEditMode: Dispatch<boolean>;
  isSuggestionMode?: boolean;
}): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  return useFloatingLinkEditorToolbar(
    editor,
    anchorElem,
    isLinkEditMode,
    setIsLinkEditMode,
    isSuggestionMode,
  );
}
